import 'katex/dist/katex.min.css';
import katex from 'katex';
import type { CSSProperties } from 'react';
import type { CollapsedSegment } from '../../utils/common-utils';

const MATH_REGEX = /\$(.+?)\$/g;

// Lexical format bitmask flags
const FORMAT_BOLD = 1;
const FORMAT_ITALIC = 2;
const FORMAT_STRIKETHROUGH = 4;
const FORMAT_UNDERLINE = 8;

const formatStyle = (format: number): CSSProperties | undefined => {
  if (format === 0) return undefined;

  const style: CSSProperties = {};

  if (format & FORMAT_BOLD) style.fontWeight = 'bold';
  if (format & FORMAT_ITALIC) style.fontStyle = 'italic';

  const decorations: string[] = [];
  if (format & FORMAT_UNDERLINE) decorations.push('underline');
  if (format & FORMAT_STRIKETHROUGH) decorations.push('line-through');
  if (decorations.length) style.textDecorationLine = decorations.join(' ');

  return style;
};

const renderMathSegments = (text: string, format: number) => {
  const parts: Array<{ type: 'text' | 'math'; value: string }> = [];
  let lastIndex = 0;

  for (const match of text.matchAll(MATH_REGEX)) {
    const matchIndex = match.index ?? 0;

    if (matchIndex > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, matchIndex) });
    }
    parts.push({ type: 'math', value: match[1] });
    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  if (parts.length === 0) {
    return <span style={formatStyle(format)}>{text}</span>;
  }

  const style = formatStyle(format);

  return (
    <span style={style}>
      {parts.map((part, i) =>
        part.type === 'text' ? (
          <span key={i}>{part.value}</span>
        ) : (
          <span
            key={i}
            dangerouslySetInnerHTML={{
              __html: katex.renderToString(part.value, {
                displayMode: false,
                throwOnError: false, errorColor: 'currentColor',
              }),
            }}
          />
        ),
      )}
    </span>
  );
};

const InlineFormattedText = ({
  segments,
}: {
  segments: CollapsedSegment[];
}) => {
  if (segments.length === 0) return null;

  return (
    <span>
      {segments.map((seg, i) => {
        if (seg.type === 'math') {
          return (
            <span
              key={i}
              dangerouslySetInnerHTML={{
                __html: katex.renderToString(
                  seg.value.replace(/^\$|\$$/g, ''),
                  { displayMode: false, throwOnError: false },
                ),
              }}
            />
          );
        }

        return (
          <span key={i}>{renderMathSegments(seg.value, seg.format)}</span>
        );
      })}
    </span>
  );
};

export default InlineFormattedText;
